import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PageLoader } from './components/ui/LoadingSpinner';
import PublicLayout from './components/layout/PublicLayout';
import DashboardLayout from './components/layout/DashboardLayout';

// Lazy-loaded public pages
const Home = lazy(() => import('./pages/public/Home'));
const Plans = lazy(() => import('./pages/public/Plans'));
const Features = lazy(() => import('./pages/public/Features'));
const Login = lazy(() => import('./pages/public/Login'));
const Register = lazy(() => import('./pages/public/Register'));
const ForgotPassword = lazy(() => import('./pages/public/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/public/ResetPassword'));
const Checkout = lazy(() => import('./pages/public/Checkout'));
const CheckoutSuccess = lazy(() => import('./pages/public/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import('./pages/public/CheckoutCancel'));
const OrderSummary = lazy(() => import('./pages/public/OrderSummary'));
const EmailVerification = lazy(() => import('./pages/public/EmailVerification'));
const EmailVerificationRequired = lazy(() => import('./pages/public/EmailVerificationRequired'));

// Lazy-loaded dashboard pages
const Overview = lazy(() => import('./pages/dashboard/Overview'));
const Services = lazy(() => import('./pages/dashboard/Services'));
const ServiceDetail = lazy(() => import('./pages/dashboard/ServiceDetail'));
const CpanelAccess = lazy(() => import('./pages/dashboard/CpanelAccess'));
const Billing = lazy(() => import('./pages/dashboard/Billing'));
const Invoices = lazy(() => import('./pages/dashboard/Invoices'));
const InvoiceDetail = lazy(() => import('./pages/dashboard/InvoiceDetail'));
const Domains = lazy(() => import('./pages/dashboard/Domains'));
const DomainDetail = lazy(() => import('./pages/dashboard/DomainDetail'));
const Tickets = lazy(() => import('./pages/dashboard/Tickets'));
const TicketDetail = lazy(() => import('./pages/dashboard/TicketDetail'));
const Settings = lazy(() => import('./pages/dashboard/Settings'));
const PaymentSuccess = lazy(() => import('./pages/dashboard/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/dashboard/PaymentCancel'));

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// GA4 page view tracking
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <RouteTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public pages with PublicLayout (nav + footer) */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/features" element={<Features />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancel" element={<CheckoutCancel />} />
              <Route path="/checkout/order-summary" element={<OrderSummary />} />
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/verify-email-required" element={<EmailVerificationRequired />} />
            </Route>

            {/* Dashboard pages with DashboardLayout (sidebar + auth guard) */}
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<Overview />} />
              <Route path="/dashboard/services" element={<Services />} />
              <Route path="/dashboard/services/:id" element={<ServiceDetail />} />
              <Route path="/dashboard/cpanel" element={<CpanelAccess />} />
              <Route path="/dashboard/billing" element={<Billing />} />
              <Route path="/dashboard/invoices" element={<Invoices />} />
              <Route path="/dashboard/invoices/:id" element={<InvoiceDetail />} />
              <Route path="/dashboard/domains" element={<Domains />} />
              <Route path="/dashboard/domains/:domain" element={<DomainDetail />} />
              <Route path="/dashboard/tickets" element={<Tickets />} />
              <Route path="/dashboard/tickets/:id" element={<TicketDetail />} />
              <Route path="/dashboard/settings" element={<Settings />} />
              <Route path="/dashboard/payment-success" element={<PaymentSuccess />} />
              <Route path="/dashboard/payment-cancel" element={<PaymentCancel />} />
            </Route>

            {/* Catch-all → home */}
            <Route path="*" element={<Home />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
